/**
 * The days that are after hours by definition: a Saturday, a Sunday, or
 * one of the tenant's recognized holidays. The note composer states the
 * fact beside its Regular | After Hours control; it never sets the
 * control, because whether the time is billed after hours is the author's
 * call.
 *
 * The holidays are tenant data in tenants.config.billing
 * .after_hours_holidays, an array of { date: 'YYYY-MM-DD', name }. They
 * are not the office-closed days in workspace.holidays, which recurring
 * tasks skip; this module never reads that list.
 */

import { parseIsoDate } from '@/lib/proseDate';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar date in YYYY-MM-DD form, parsed at local midnight, or
 *  null (a malformed string, or one that would roll over, like 30 Feb). */
function realDate(iso: string): Date | null {
  if (!ISO_DATE.test(iso)) return null;
  const d = parseIsoDate(iso);
  const [y, m, day] = iso.split('-').map(Number);
  if (d.getFullYear() !== y || d.getMonth() + 1 !== m || d.getDate() !== day) return null;
  return d;
}

/** The after-hours holidays, date -> name. Source: tenants.config.billing
 *  .after_hours_holidays, an array of { date: 'YYYY-MM-DD', name }. An entry
 *  with a malformed date or an empty name is dropped; an unset or malformed
 *  list is an empty map, never an error. */
export function recognizedHolidays(config: unknown): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  if (config === null || typeof config !== 'object') return map;
  const billing = (config as Record<string, unknown>).billing;
  if (billing === null || typeof billing !== 'object') return map;
  const list = (billing as Record<string, unknown>).after_hours_holidays;
  if (!Array.isArray(list)) return map;
  for (const entry of list) {
    if (entry === null || typeof entry !== 'object') continue;
    const { date, name } = entry as Record<string, unknown>;
    if (typeof date !== 'string' || realDate(date) === null) continue;
    if (typeof name !== 'string' || name.trim() === '') continue;
    map.set(date, name.trim());
  }
  return map;
}

export type AfterHoursDay =
  | { kind: 'holiday'; name: string }
  | { kind: 'weekend'; weekday: 'Saturday' | 'Sunday' };

/** { kind: 'holiday', name } when the date is in the map (a holiday on a
 *  weekend reads holiday), { kind: 'weekend', weekday } on a Saturday or
 *  Sunday, otherwise null; null for anything that is not a real YYYY-MM-DD
 *  date. The parse is at local midnight, so the weekday is the calendar
 *  date's own in every time zone. */
export function afterHoursDay(date: string, holidays: ReadonlyMap<string, string>): AfterHoursDay | null {
  const d = realDate(date);
  if (d === null) return null;
  const name = holidays.get(date);
  if (name !== undefined) return { kind: 'holiday', name };
  const weekday = d.getDay();
  if (weekday === 6) return { kind: 'weekend', weekday: 'Saturday' };
  if (weekday === 0) return { kind: 'weekend', weekday: 'Sunday' };
  return null;
}
